import { openReportWithAuth } from "../util";
import type { PaginatedResponse } from "./courseCertificates";
import { cleApi, getCleApiPrefix } from "./client";
import type { TeachingEvidenceModel } from "./models/teachings";
import axios from "axios";

export type {
  TeachingEvidenceListModel,
  TeachingEvidenceModel,
} from "./models/teachings";

export interface SupportDocumentModel {
  id: string;
  fileName: string | null;
  uploadedById: string;
  uploadedAt: string;
  updatedAt: string;
  reported: boolean;
  reportId: string | null;
  status: string;
}

/** @deprecated Prefer TeachingEvidenceModel for detail views */
export interface TeachingDocumentModel extends SupportDocumentModel {
  type: "teaching";
  numberOfLessons: number;
}

export interface GetTeachingsParams {
  userId: string;
  page?: number;
  pageSize?: number;
  reported?: boolean;
}

export interface GetTeachingByIdParams {
  userId: string;
  teachingId: string;
}

export interface GetTeachingsByPeriodParams {
  userId: string;
  periodId: string;
}

export interface GetTeachingsByInjunctionParams {
  userId: string;
  injunctionId: string;
}

export interface GetTeachingsByImpositionParams {
  userId: string;
  impositionId: string;
}

export interface UpdateTeachingRequest {
  description?: string | null;
  number_of_lessons?: number;
}

export interface UpdateTeachingParams {
  userId: string;
  teachingId: string;
  body: UpdateTeachingRequest;
}

export const getTeachings = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: GetTeachingsParams): Promise<PaginatedResponse<TeachingDocumentModel>> => {
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });

  if (reported !== undefined) {
    query.set("reported", String(reported));
  }

  return cleApi.get<PaginatedResponse<TeachingDocumentModel>>(
    `users/${encodeURIComponent(userId)}/teachings?${query.toString()}`,
  );
};

export const getTeachingById = ({
  userId,
  teachingId,
}: GetTeachingByIdParams): Promise<TeachingEvidenceModel> =>
  cleApi.get<TeachingEvidenceModel>(
    `users/${encodeURIComponent(userId)}/teachings/${encodeURIComponent(
      teachingId,
    )}`,
  );

export const updateTeaching = ({
  userId,
  teachingId,
  body,
}: UpdateTeachingParams): Promise<TeachingEvidenceModel> =>
  cleApi.put<TeachingEvidenceModel>(
    `users/${encodeURIComponent(userId)}/teachings/${encodeURIComponent(teachingId)}`,
    body,
  );

export const getTeachingsByPeriodId = ({
  userId,
  periodId,
}: GetTeachingsByPeriodParams): Promise<TeachingDocumentModel[]> =>
  cleApi.get<TeachingDocumentModel[]>(
    `users/${encodeURIComponent(userId)}/teachings/by-period/${encodeURIComponent(periodId)}`,
  );

export const getTeachingsByInjunctionId = ({
  userId,
  injunctionId,
}: GetTeachingsByInjunctionParams): Promise<TeachingDocumentModel[]> =>
  cleApi.get<TeachingDocumentModel[]>(
    `users/${encodeURIComponent(userId)}/teachings/by-injunction/${encodeURIComponent(injunctionId)}`,
  );

export const getTeachingsByImpositionId = ({
  userId,
  impositionId,
}: GetTeachingsByImpositionParams): Promise<TeachingDocumentModel[]> =>
  cleApi.get<TeachingDocumentModel[]>(
    `users/${encodeURIComponent(userId)}/teachings/by-imposition/${encodeURIComponent(impositionId)}`,
  );

export const openTeachingDownload = ({
  userId,
  teachingId,
}: GetTeachingByIdParams): Promise<void> =>
  openReportWithAuth(
    `${getCleApiPrefix()}users/${encodeURIComponent(
      userId,
    )}/teachings/${encodeURIComponent(teachingId)}/download`,
  );

export const getTeachingPdfBlob = async ({
  userId,
  teachingId,
}: GetTeachingByIdParams): Promise<Blob> => {
  const response = await axios.get<Blob>(
    `${getCleApiPrefix()}users/${encodeURIComponent(
      userId,
    )}/teachings/${encodeURIComponent(teachingId)}/download`,
    { responseType: "blob" },
  );

  return response.data;
};

export interface DownloadTeachingAttachmentParams {
  userId: string;
  teachingId: string;
  attachmentId: string;
}

const teachingAttachmentDownloadUrl = ({
  userId,
  teachingId,
  attachmentId,
}: DownloadTeachingAttachmentParams): string =>
  `${getCleApiPrefix()}users/${encodeURIComponent(
    userId,
  )}/teachings/${encodeURIComponent(
    teachingId,
  )}/attachments/${encodeURIComponent(attachmentId)}/download`;

/** Bilag på undervisning (ADV-359), som på kursusbeviser. */
export const openTeachingAttachmentDownload = (
  params: DownloadTeachingAttachmentParams,
): Promise<void> => openReportWithAuth(teachingAttachmentDownloadUrl(params));

export const getTeachingAttachmentBlob = async (
  params: DownloadTeachingAttachmentParams,
): Promise<Blob> => {
  const response = await axios.get<Blob>(
    teachingAttachmentDownloadUrl(params),
    { responseType: "blob" },
  );

  return response.data;
};
